import { Component } from '@theme/component';

/**
 * Draws a wavy transition edge between the section above (top color) and the section below
 * (bottom color), using the same cubic-bezier "S-curve" construction as the wavy marquee.
 *
 * @typedef {object} Refs
 * @property {SVGSVGElement} svg - The SVG element the divider is drawn into.
 *
 * @extends Component<Refs>
 */
class WavyDividerComponent extends Component {
  requiredRefs = ['svg'];

  connectedCallback() {
    super.connectedCallback();

    this.#build();

    this.#resizeObserver = new ResizeObserver(this.#handleResize);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    if (this.#rafId !== null) cancelAnimationFrame(this.#rafId);
  }

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number} */
  #lastWidth = 0;

  /** @type {number | null} */
  #rafId = null;

  // Rebuilt on the next frame rather than debounced: the SVG already stretches with its container
  // (see the stylesheet), and this snaps the exact geometry in right behind it, so there's no
  // window where a stale, wrong-sized wave exposes a straight edge while the viewport changes.
  #handleResize = () => {
    if (this.#rafId !== null) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = null;
      if (Math.abs(this.clientWidth - this.#lastWidth) < 1) return;
      this.#build();
    });
  };

  #build() {
    const { svg } = this.refs;

    const containerWidth = this.clientWidth || 800;
    this.#lastWidth = containerWidth;

    // Same proportions as the reference wave path: 2 full cycles across the width, amplitude
    // scaling with the container width so the curve looks the same at any breakpoint.
    const cycles = 2;
    const wavelength = containerWidth / cycles;
    const halfWavelength = wavelength / 2;
    const totalHalfCycles = cycles * 2;
    const amplitude = this.clientWidth <= 500 ? Math.max(6, containerWidth * 0.030) :  Math.max(6, containerWidth * 0.026);
    const strokeWidth = 2;
    // Extra height reserves half the stroke width above and below the wave so the outline
    // isn't clipped at the top/bottom edges of the SVG when the peaks touch y=0 or y=height.
    const height = Math.ceil(amplitude * 2 + strokeWidth);
    const midY = height / 2;

    const topY = midY - amplitude;
    const bottomY = midY + amplitude;
    const yAt = (/** @type {number} */ k) => (k % 2 === 0 ? topY : bottomY);

    const segments = buildWaveSegments(halfWavelength, totalHalfCycles, yAt);
    const curvePath = segmentsToPath(segments);
    const last = segments[segments.length - 1];

    // Everything above the curve is the "top" fill; the plain rect beneath it shows through as
    // the "bottom" fill wherever the curve dips below it.
    const topFillPath = last
      ? `M0,0 L${containerWidth},0 L${last.x1.toFixed(2)},${last.y1.toFixed(2)}${segmentsToReversedCommands(segments)} Z`
      : '';

    // Only a viewBox — no fixed width/height — so the SVG scales with the container's width (height
    // follows the viewBox's aspect ratio). The wave's proportions are width-relative anyway, so
    // the scaled drawing is effectively exact until the next rebuild lands.
    svg.setAttribute('viewBox', `0 0 ${containerWidth} ${height}`);

    svg.innerHTML = [
      `<rect x="0" y="0" width="${containerWidth}" height="${height}" fill="var(--wavy-divider-bottom, currentColor)"></rect>`,
      `<path d="${topFillPath}" fill="var(--wavy-divider-top, transparent)"></path>`,
      `<path d="${curvePath}" fill="none" stroke="var(--wavy-divider-outline)" stroke-width="${strokeWidth}" vector-effect="non-scaling-stroke"></path>`,
    ].join('');
  }
}

/**
 * @typedef {object} WaveSegment
 * @property {number} x0
 * @property {number} y0
 * @property {number} cx1
 * @property {number} cy1
 * @property {number} cx2
 * @property {number} cy2
 * @property {number} x1
 * @property {number} y1
 */

/**
 * Builds a wavy line as a series of cubic-bezier "S-curve" half-cycles: each control point sits
 * level with its nearest endpoint, offset horizontally by half the half-wavelength.
 *
 * @param {number} halfWavelength
 * @param {number} totalHalfCycles
 * @param {function(number): number} yAt - y-coordinate of the curve at half-cycle index k.
 * @returns {WaveSegment[]}
 */
function buildWaveSegments(halfWavelength, totalHalfCycles, yAt) {
  /** @type {WaveSegment[]} */
  const segments = [];

  for (let h = 1; h <= totalHalfCycles; h++) {
    const x0 = (h - 1) * halfWavelength;
    const x1 = h * halfWavelength;
    const y0 = yAt(h - 1);
    const y1 = yAt(h);

    segments.push({
      x0,
      y0,
      cx1: x0 + halfWavelength / 2,
      cy1: y0,
      cx2: x1 - halfWavelength / 2,
      cy2: y1,
      x1,
      y1,
    });
  }

  return segments;
}

/**
 * @param {WaveSegment[]} segments
 */
function segmentsToPath(segments) {
  const first = segments[0];
  if (!first) return '';

  let d = `M${first.x0.toFixed(2)},${first.y0.toFixed(2)}`;

  for (const s of segments) {
    d += ` C${s.cx1.toFixed(2)},${s.cy1.toFixed(2)} ${s.cx2.toFixed(2)},${s.cy2.toFixed(2)} ${s.x1.toFixed(2)},${s.y1.toFixed(2)}`;
  }

  return d;
}

/**
 * Renders the same segments as cubic-bezier commands traversed back to front, without a leading
 * "M" — used to trace the curve backwards while closing the top-fill shape.
 *
 * @param {WaveSegment[]} segments
 */
function segmentsToReversedCommands(segments) {
  let d = '';

  for (let i = segments.length - 1; i >= 0; i--) {
    const s = segments[i];
    if (!s) continue;
    d += ` C${s.cx2.toFixed(2)},${s.cy2.toFixed(2)} ${s.cx1.toFixed(2)},${s.cy1.toFixed(2)} ${s.x0.toFixed(2)},${s.y0.toFixed(2)}`;
  }

  return d;
}

if (!customElements.get('wavy-divider')) {
  customElements.define('wavy-divider', WavyDividerComponent);
}
